import { doc, getDoc, setDoc, updateDoc, collection, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Obtém a chave do mês a partir da data do agendamento (formato: YYYY-MM)
 */
const getMonthKey = (appointmentDate) => {
  const date = appointmentDate?.toDate ? appointmentDate.toDate() : new Date(appointmentDate);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

/**
 * Atualiza o agregado mensal de agendamentos quando um agendamento é criado
 */
export const updateAppointmentsAggregationOnCreate = async (appointmentData) => {
  try {
    if (!appointmentData || !appointmentData.id || !appointmentData.dataAgendamento) {
      return;
    }

    const appointmentDate = appointmentData.dataAgendamento;
    const monthKey = getMonthKey(appointmentDate);
    const appointmentsAggregateRef = doc(db, 'agregadoAgendamentos', monthKey);
    const appointmentsAggregateDoc = await getDoc(appointmentsAggregateRef);
    
    const appointmentEntry = {
      id: appointmentData.id,
      ...appointmentData,
      dataAgendamento: appointmentData.dataAgendamento instanceof Date 
        ? Timestamp.fromDate(appointmentData.dataAgendamento) 
        : (appointmentData.dataAgendamento?.toDate 
            ? appointmentData.dataAgendamento 
            : Timestamp.fromDate(new Date(appointmentData.dataAgendamento)))
    };
    
    if (appointmentsAggregateDoc.exists()) {
      const currentData = appointmentsAggregateDoc.data();
      const appointments = currentData.appointments || [];
      
      const existingIndex = appointments.findIndex(a => a.id === appointmentData.id);
      
      if (existingIndex === -1) {
        appointments.push(appointmentEntry);
        // Ordenar por data (mais recente primeiro)
        appointments.sort((a, b) => {
          const dateA = a.dataAgendamento?.toDate ? a.dataAgendamento.toDate() : new Date(a.dataAgendamento);
          const dateB = b.dataAgendamento?.toDate ? b.dataAgendamento.toDate() : new Date(b.dataAgendamento);
          return dateB.getTime() - dateA.getTime();
        });
        
        await updateDoc(appointmentsAggregateRef, {
          appointments: appointments,
          quantidadeTotal: appointments.length,
          ultimaAtualizacao: Timestamp.now()
        });
      } else {
        // Agendamento já existe no agregado, atualizando
        appointments[existingIndex] = appointmentEntry;
        appointments.sort((a, b) => {
          const dateA = a.dataAgendamento?.toDate ? a.dataAgendamento.toDate() : new Date(a.dataAgendamento);
          const dateB = b.dataAgendamento?.toDate ? b.dataAgendamento.toDate() : new Date(b.dataAgendamento);
          return dateB.getTime() - dateA.getTime();
        });
        await updateDoc(appointmentsAggregateRef, {
          appointments: appointments,
          quantidadeTotal: appointments.length,
          ultimaAtualizacao: Timestamp.now()
        });
      }
    } else {
      // Criar novo agregado mensal
      const date = appointmentDate?.toDate ? appointmentDate.toDate() : new Date(appointmentDate);
      await setDoc(appointmentsAggregateRef, {
        ano: date.getFullYear(),
        mes: date.getMonth() + 1,
        appointments: [appointmentEntry],
        quantidadeTotal: 1,
        ultimaAtualizacao: Timestamp.now()
      });
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Atualiza o agregado mensal de agendamentos quando um agendamento é editado
 * Se a data mudou de mês, remove do agregado antigo e adiciona no novo
 */
export const updateAppointmentsAggregationOnUpdate = async (oldAppointmentData, newAppointmentData) => {
  try {
    if (!newAppointmentData || !newAppointmentData.id || !newAppointmentData.dataAgendamento) {
      return;
    }

    const newAppointmentDate = newAppointmentData.dataAgendamento;
    const oldAppointmentDate = oldAppointmentData?.dataAgendamento;
    const newMonthKey = getMonthKey(newAppointmentDate);
    const oldMonthKey = oldAppointmentDate ? getMonthKey(oldAppointmentDate) : null;
    
    // Se mudou de mês, remover do agregado antigo e adicionar no novo
    if (oldMonthKey && oldMonthKey !== newMonthKey) {
      // Remover do agregado antigo
      const oldAggregateRef = doc(db, 'agregadoAgendamentos', oldMonthKey);
      const oldAggregateDoc = await getDoc(oldAggregateRef);
      
      if (oldAggregateDoc.exists()) {
        const oldData = oldAggregateDoc.data();
        const oldAppointments = oldData.appointments || [];
        const filteredAppointments = oldAppointments.filter(a => a.id !== newAppointmentData.id);
        
        if (filteredAppointments.length !== oldAppointments.length) {
          if (filteredAppointments.length > 0) {
            await updateDoc(oldAggregateRef, {
              appointments: filteredAppointments,
              quantidadeTotal: filteredAppointments.length,
              ultimaAtualizacao: Timestamp.now()
            });
          } else {
            // Se não há mais agendamentos, manter o agregado mas vazio
            await updateDoc(oldAggregateRef, {
              appointments: [],
              quantidadeTotal: 0,
              ultimaAtualizacao: Timestamp.now()
            });
          }
        }
      }
      
      // Adicionar no novo agregado mensal
      await updateAppointmentsAggregationOnCreate(newAppointmentData);
    } else {
      // Mesmo mês, apenas atualizar no agregado
      const appointmentsAggregateRef = doc(db, 'agregadoAgendamentos', newMonthKey);
      const appointmentsAggregateDoc = await getDoc(appointmentsAggregateRef);
      
      if (appointmentsAggregateDoc.exists()) {
        const currentData = appointmentsAggregateDoc.data();
        const appointments = currentData.appointments || [];
        
        const appointmentIndex = appointments.findIndex(a => a.id === newAppointmentData.id);
        
        const appointmentEntry = {
          id: newAppointmentData.id,
          ...newAppointmentData,
          dataAgendamento: newAppointmentData.dataAgendamento instanceof Date 
            ? Timestamp.fromDate(newAppointmentData.dataAgendamento) 
            : newAppointmentData.dataAgendamento
        };
        
        if (appointmentIndex !== -1) {
          appointments[appointmentIndex] = appointmentEntry;
        } else {
          appointments.push(appointmentEntry);
        }
        
        // Reordenar por data
        appointments.sort((a, b) => {
          const dateA = a.dataAgendamento?.toDate ? a.dataAgendamento.toDate() : new Date(a.dataAgendamento);
          const dateB = b.dataAgendamento?.toDate ? b.dataAgendamento.toDate() : new Date(b.dataAgendamento);
          return dateB.getTime() - dateA.getTime();
        });
        
        await updateDoc(appointmentsAggregateRef, {
          appointments: appointments,
          quantidadeTotal: appointments.length,
          ultimaAtualizacao: Timestamp.now()
        });
      } else {
        // Agregado não existe, criar novo
        await updateAppointmentsAggregationOnCreate(newAppointmentData);
      }
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Atualiza o agregado mensal de agendamentos quando um agendamento é deletado
 * Precisa buscar o agendamento para saber qual mês remover
 */
export const updateAppointmentsAggregationOnDelete = async (appointmentId, appointmentDate) => {
  try {
    if (!appointmentId) {
      return;
    }

    // Se não temos a data, precisamos buscar o agendamento
    let monthKey = null;
    if (appointmentDate) {
      monthKey = getMonthKey(appointmentDate);
    } else {
      // Buscar o agendamento para obter a data
      const appointmentRef = doc(db, 'agendamentos', appointmentId);
      const appointmentDoc = await getDoc(appointmentRef);
      if (appointmentDoc.exists()) {
        const appointmentData = appointmentDoc.data();
        if (appointmentData.dataAgendamento) {
          monthKey = getMonthKey(appointmentData.dataAgendamento);
        }
      }
    }

    if (!monthKey) {
      // Não foi possível determinar o mês, buscar em todos os agregados mensais
      // Por enquanto, vamos apenas retornar sem erro
      return;
    }

    const appointmentsAggregateRef = doc(db, 'agregadoAgendamentos', monthKey);
    const appointmentsAggregateDoc = await getDoc(appointmentsAggregateRef);
    
    if (appointmentsAggregateDoc.exists()) {
      const currentData = appointmentsAggregateDoc.data();
      const appointments = currentData.appointments || [];
      
      const filteredAppointments = appointments.filter(a => a.id !== appointmentId);
      
      if (filteredAppointments.length !== appointments.length) {
        if (filteredAppointments.length > 0) {
          await updateDoc(appointmentsAggregateRef, {
            appointments: filteredAppointments,
            quantidadeTotal: filteredAppointments.length,
            ultimaAtualizacao: Timestamp.now()
          });
        } else {
          // Se não há mais agendamentos, manter o agregado mas vazio
          await updateDoc(appointmentsAggregateRef, {
            appointments: [],
            quantidadeTotal: 0,
            ultimaAtualizacao: Timestamp.now()
          });
        }
      }
    }
  } catch (error) {
    throw error;
  }
};
